import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import WeddingPasswordPrompt from '../components/WeddingPasswordPrompt';
import { ArrowLeft, Download, Loader, ChevronLeft, ChevronRight, X, Camera, CheckSquare, Square, Heart } from 'lucide-react';
import './WeddingDetail.css';

const API_URL = 'https://image-upload-api-gamma.vercel.app/api/images';

const fixImageUrl = (url) => {
  return url ? url.replace(/([^:]\/)\/+/g, "$1") : url;
};

// Helper function to extract number from filename for sorting
const extractNumberFromFilename = (filename) => {
  if (!filename) return 0;

  // Extract numbers from filename (e.g., "image1.jpg" -> 1, "photo_23.png" -> 23)
  const match = filename.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 0;
};

function WeddingDetail() {
  // Get category from URL params or search params
  const { galleryName, category: routeCategory } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const imageCardsRef = useRef([]);
  const [headerVisible, setHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);

  const decodedWeddingName = decodeURIComponent(galleryName);
  const initialCategory = routeCategory || searchParams.get('category');

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(initialCategory || null);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [bulkDownloadMode, setBulkDownloadMode] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  // showFavorites state removed as it was unused

  // Touch handling for Swipe
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;

    if (Math.abs(distance) < minSwipeDistance) return;

    if (distance > 0) {
      // Swipe Left -> Next Image
      navigateLightbox('next', displayImages);
    } else {
      // Swipe Right -> Prev Image
      navigateLightbox('prev', displayImages);
    }
  };


  // Handle scroll behavior for header
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;

          if (currentScrollY < lastScrollY.current) {
            setHeaderVisible(true);
          } else if (currentScrollY > 100 && currentScrollY > lastScrollY.current) {
            setHeaderVisible(false);
          }

          lastScrollY.current = currentScrollY;
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadWeddingImages = useCallback(async () => {
    try {
      setLoading(true);
      // Use the new serverless API that filters by wedding name
      const { data } = await axios.get(`${API_URL}/gallery`, {
        params: { weddingName: decodedWeddingName }
      });
      setMetadata(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load wedding images:', err);
      setError('Failed to load images. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [decodedWeddingName]);

  const verifyStoredPassword = useCallback(
    async (password) => {
      try {
        const response = await fetch(`${API_URL}/verify-wedding-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            weddingName: decodedWeddingName,
            password
          })
        });

        const data = await response.json();

        if (data.valid) {
          setIsAuthenticated(true);
          loadWeddingImages();
        } else {
          sessionStorage.removeItem(`wedding_${decodedWeddingName}`);
        }
      } catch (err) {
        sessionStorage.removeItem(`wedding_${decodedWeddingName}`);
      } finally {
        setCheckingAuth(false);
      }
    },
    [decodedWeddingName, loadWeddingImages]
  );

  // Load favorites from local storage
  useEffect(() => {
    if (decodedWeddingName) {
      const storedFavorites = localStorage.getItem(`favorites_${decodedWeddingName}`);
      if (storedFavorites) {
        try {
          const parsed = JSON.parse(storedFavorites);
          setFavorites(new Set(parsed));
        } catch (e) {
          console.error("Failed to parse favorites", e);
        }
      }
    }
  }, [decodedWeddingName]);

  const toggleFavorite = (imageUrl, e) => {
    if (e) e.stopPropagation();

    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(imageUrl)) {
        newFavorites.delete(imageUrl);
      } else {
        newFavorites.add(imageUrl);
      }

      // Save to local storage
      localStorage.setItem(`favorites_${decodedWeddingName}`, JSON.stringify(Array.from(newFavorites)));

      return newFavorites;
    });
  };

  // Calculate wedding images and categories using useMemo
  const weddingImages = useMemo(() => {
    // Metadata now returns only images for this wedding, so strictly speaking
    // we just need to ensure we are using the array.
    // However, keeping a safety check doesn't hurt.
    const images = metadata?.images || [];

    // Legacy check just in case, but the API should have filtered it.
    // We can filter out non-wedding images if any (unlikely with new API).
    // Sort by numerical order from filename (e.g., image1, image2, image3)
    return images.sort((a, b) => {
      const numA = extractNumberFromFilename(a.name);
      const numB = extractNumberFromFilename(b.name);

      // If numbers are the same, fall back to upload time
      if (numA === numB) {
        const dateA = new Date(a.uploadedAt || 0);
        const dateB = new Date(b.uploadedAt || 0);
        return dateA - dateB;
      }

      return numA - numB;
    });
  }, [metadata]);

  const categorizedImages = useMemo(() => {
    return weddingImages.reduce((acc, img) => {
      const parts = img.category.split('/');
      const category = parts[2] || 'other';

      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(img);
      return acc;
    }, {});
  }, [weddingImages]);

  const categories = useMemo(() => {
    return Object.keys(categorizedImages).sort();
  }, [categorizedImages]);

  // Get cover images from metadata (marked with isCover: true)
  const categoryCoverImages = useMemo(() => {
    const covers = {};

    metadata?.images?.forEach(img => {
      if (img.isCover && img.category?.includes('/')) {
        const parts = img.category.split('/');
        const weddingNameFromPath = parts[1];
        const category = parts[2];

        if (weddingNameFromPath === decodedWeddingName) {
          covers[category] = img;
        }
      }
    });

    // Fallback to first image if no cover photo exists
    Object.keys(categorizedImages).forEach(category => {
      if (!covers[category] && categorizedImages[category].length > 0) {
        covers[category] = categorizedImages[category][0];
      }
    });

    return covers;
  }, [metadata, decodedWeddingName, categorizedImages]);

  // Get main wedding cover photo (for meta tags)
  const mainCoverPhoto = useMemo(() => {
    const mainCover = metadata?.images?.find(img =>
      img.isCover &&
      img.category?.includes('/') &&
      img.category.split('/')[1] === decodedWeddingName &&
      img.category.split('/')[2] === 'main'
    );

    // Fallback to first category cover if no main cover exists
    if (!mainCover && Object.keys(categoryCoverImages).length > 0) {
      const firstCategory = Object.keys(categoryCoverImages)[0];
      return categoryCoverImages[firstCategory];
    }

    return mainCover;
  }, [metadata, decodedWeddingName, categoryCoverImages]);

  // Get category labels from metadata
  const categoryLabels = useMemo(() => {
    return metadata?.categoryLabels || {};
  }, [metadata]);

  const displayImages = useMemo(() => {
    if (selectedCategory === 'all') {
      return weddingImages;
    }
    if (selectedCategory === 'favorites') {
      return weddingImages.filter(img => favorites.has(img.url));
    }
    if (!selectedCategory) {
      return [];
    }
    return categorizedImages[selectedCategory] || [];
  }, [selectedCategory, categorizedImages, weddingImages, favorites]);


  // Effect to handle Category change from Route or Params
  useEffect(() => {
    const currentCategory = routeCategory || searchParams.get('category');
    if (currentCategory === 'all' || currentCategory === 'favorites') {
      setSelectedCategory(currentCategory);
    } else if (currentCategory && categories.includes(currentCategory)) {
      setSelectedCategory(currentCategory);
    }
  }, [routeCategory, searchParams, categories]);


  // Auth Logic: Bypass if entering deeply linked category, else check password
  useEffect(() => {
    if (routeCategory) {
      setIsAuthenticated(true);
      setCheckingAuth(false);
      loadWeddingImages();
      return;
    }

    const storedPassword = sessionStorage.getItem(`wedding_${decodedWeddingName}`);
    if (storedPassword) {
      verifyStoredPassword(storedPassword);
    } else {
      setCheckingAuth(false);
    }
  }, [decodedWeddingName, routeCategory, verifyStoredPassword, loadWeddingImages]);

  useEffect(() => {
    if (!metadata) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    imageCardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [metadata, selectedCategory]);

  useEffect(() => {
    if (!lightboxImage || !displayImages.length) return;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        const newIndex = (lightboxIndex - 1 + displayImages.length) % displayImages.length;
        setLightboxIndex(newIndex);
        setLightboxImage(displayImages[newIndex]);
      } else if (e.key === 'ArrowRight') {
        const newIndex = (lightboxIndex + 1) % displayImages.length;
        setLightboxIndex(newIndex);
        setLightboxImage(displayImages[newIndex]);
      } else if (e.key === 'Escape') {
        setLightboxImage(null);
        document.body.style.overflow = 'auto';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, lightboxIndex, displayImages]);

  const handlePasswordSuccess = useCallback(() => {
    setIsAuthenticated(true);
    loadWeddingImages();
  }, [loadWeddingImages]);

  const handleDownload = async (imageUrl, imageName) => {
    try {
      const fixedUrl = fixImageUrl(imageUrl);
      const downloadUrl = `${API_URL}/download?url=${encodeURIComponent(fixedUrl)}&t=${Date.now()}`;

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = imageName;
      link.target = '_blank';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
      }, 5000);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download image. Please try again.');
    }
  };

  const handleBulkDownload = async () => {
    if (selectedImages.size === 0) {
      alert('Please select at least one image');
      return;
    }

    const confirmed = window.confirm(`Download ${selectedImages.size} selected image(s) as ZIP?`);
    if (!confirmed) return;

    setDownloading(true);

    try {
      const imagesToDownload = Array.from(selectedImages).map(index => ({
        url: fixImageUrl(displayImages[index].url),
        name: displayImages[index].name
      }));

      const categoryLabel = categoryLabels[selectedCategory] || selectedCategory;
      const zipName = `${decodedWeddingName}-${categoryLabel}-${imagesToDownload.length}-images.zip`;

      const response = await fetch(`${API_URL}/bulk-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          images: imagesToDownload,
          zipName: zipName
        })
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = zipName;
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 100);

      alert(`✅ Downloaded ${imagesToDownload.length} image(s) as ZIP!`);
    } catch (error) {
      console.error('Bulk download failed:', error);
      alert('Failed to download images as ZIP. Please try again.');
    } finally {
      setDownloading(false);
      setSelectedImages(new Set());
      setBulkDownloadMode(false);
    }
  };

  const toggleImageSelection = (index) => {
    setSelectedImages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const selectAllImages = () => {
    if (selectedImages.size === displayImages.length) {
      setSelectedImages(new Set());
    } else {
      setSelectedImages(new Set(displayImages.map((_, i) => i)));
    }
  };

  const openLightbox = useCallback((img, allImages) => {
    const index = allImages.findIndex(i => i.url === img.url);
    setLightboxImage(img);
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
    document.body.style.overflow = 'auto';
  }, []);

  const navigateLightbox = useCallback((direction, allImages) => {
    const newIndex = direction === 'next'
      ? (lightboxIndex + 1) % allImages.length
      : (lightboxIndex - 1 + allImages.length) % allImages.length;

    setLightboxIndex(newIndex);
    setLightboxImage(allImages[newIndex]);
  }, [lightboxIndex]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    // If we are in deep link mode, navigate to the category URL
    if (routeCategory) {
      navigate(`/gallery-detail/${galleryName}/${category}`);
    } else {
      setSearchParams({ category });
    }

    setSelectedImages(new Set());
    setBulkDownloadMode(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    if (routeCategory) {
      navigate(`/gallery-detail/${galleryName}`);
    } else {
      setSearchParams({});
    }
    setSelectedImages(new Set());
    setBulkDownloadMode(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleImageLoad = (imageUrl) => {
    setLoadedImages(prev => new Set([...prev, imageUrl]));
  };

  if (checkingAuth) {
    return (
      <div className="wedding-loading">
        <Loader className="spin" size={48} />
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <WeddingPasswordPrompt
        weddingName={decodedWeddingName}
        onSuccess={handlePasswordSuccess}
      />
    );
  }

  if (loading) {
    return (
      <div className="wedding-loading">
        <Loader className="spin" size={48} />
        <p>Loading gallery...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wedding-error">
        <p>{error}</p>
        <button onClick={loadWeddingImages} className="back-gallery-btn">
          Try Again
        </button>
      </div>
    );
  }

  if (weddingImages.length === 0) {
    return (
      <div className="wedding-detail-container">
        <div className={`wedding-detail-header ${headerVisible ? 'visible' : 'hidden'}`}>
          <button onClick={() => navigate(-1)} className="back-btn">
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="wedding-info">
            <h1>{decodedWeddingName}</h1>
            <p className="photo-count">0 Photos</p>
          </div>
          <div className="tagteam-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>TagTeam Studios</div>
        </div>
        <div className="wedding-error">
          <p>No images found for this wedding</p>
          <button onClick={() => navigate(-1)} className="back-gallery-btn">
            Back to Gallery
          </button>
        </div>
      </div>
    );
  }

  // Prepare meta tag values
  const pageTitle = `${decodedWeddingName} Wedding by tagteam studios`;
  const pageDescription = `View ${decodedWeddingName}'s wedding photos by TagTeam Studios - Professional Wedding Photography and Videography`;
  const pageUrl = `https://tagteamstudios.in/gallery-detail/${encodeURIComponent(decodedWeddingName)}`;
  const pageImage = mainCoverPhoto ? fixImageUrl(mainCoverPhoto.url) : 'https://ik.imagekit.io/p3xaqnkci/tagteam/tts_new_logos.webp';

  // Show category grid if no category selected
  if (!selectedCategory) {
    return (
      <div className="wedding-detail-container">
        <Helmet>
          <title>{pageTitle}</title>
          <meta name="description" content={pageDescription} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:image" content={pageImage} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:type" content="website" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={pageDescription} />
          <meta name="twitter:image" content={pageImage} />
        </Helmet>
        <div className={`wedding-detail-header ${headerVisible ? 'visible' : 'hidden'}`}>
          <button onClick={() => navigate(-1)} className="back-btn">
            <ArrowLeft size={20} />
            Back
          </button>
          <div className="wedding-info">
            <h1>{decodedWeddingName}</h1>
            <p className="photo-count">{weddingImages.length} Photos · {categories.length} Albums</p>
          </div>
          <div className="tagteam-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>TagTeam Studios</div>
        </div>

        <div className="category-grid-container">
          <h2 className="category-grid-title">Albums</h2>
          <div className="category-grid">
            {categories.map((category, index) => (
              <div
                key={category}
                className="category-card"
                onClick={() => handleCategorySelect(category)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="category-cover">
                  {categoryCoverImages[category] && (
                    <img
                      src={fixImageUrl(categoryCoverImages[category].url)}
                      alt={categoryLabels[category] || category}
                      loading="lazy"
                    />
                  )}
                  <div className="category-overlay">
                    <Camera size={32} />
                  </div>
                </div>
                <div className="category-info">
                  <h3>{categoryLabels[category] || category}</h3>
                  <p>{categorizedImages[category].length} Photos</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show selected category images
  const categoryTitle = `${categoryLabels[selectedCategory] || selectedCategory} - ${decodedWeddingName} Wedding by tagteam studios`;
  const categoryDescription = `View ${categoryLabels[selectedCategory] || selectedCategory} photos from ${decodedWeddingName}'s wedding by TagTeam Studios`;
  const categoryUrl = `https://tagteamstudios.in/gallery-detail/${encodeURIComponent(decodedWeddingName)}/${encodeURIComponent(selectedCategory)}`;
  const categoryImage = categoryCoverImages[selectedCategory] ? fixImageUrl(categoryCoverImages[selectedCategory].url) : pageImage;

  return (
    <div className="wedding-detail-container">
      <Helmet>
        <title>{categoryTitle}</title>
        <meta name="description" content={categoryDescription} />
        <meta property="og:title" content={categoryTitle} />
        <meta property="og:description" content={categoryDescription} />
        <meta property="og:image" content={categoryImage} />
        <meta property="og:url" content={categoryUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={categoryTitle} />
        <meta name="twitter:description" content={categoryDescription} />
        <meta name="twitter:image" content={categoryImage} />
      </Helmet>
      <div className={`wedding-detail-header ${headerVisible ? 'visible' : 'hidden'}`}>
        <button onClick={handleBackToCategories} className="back-btn">
          <ArrowLeft size={20} />
          Back to Albums
        </button>
        <div className="wedding-info">
          <h1>
            {selectedCategory === 'all' ? 'All Photos' :
              selectedCategory === 'favorites' ? 'My Favourites' :
                (categoryLabels[selectedCategory] || selectedCategory)}
          </h1>
        </div>
        <div className="tagteam-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>TagTeam Studios</div>
      </div>

      {/* Bulk Download Controls */}
      {displayImages.length > 0 && (
        <div className="bulk-download-controls">
          <button
            onClick={() => {
              setBulkDownloadMode(!bulkDownloadMode);
              setSelectedImages(new Set());
            }}
            className={`bulk-select-toggle ${bulkDownloadMode ? 'active' : ''}`}
          >
            {bulkDownloadMode ? <CheckSquare size={18} /> : <Square size={18} />}
            {bulkDownloadMode ? 'Cancel Selection' : 'Select Images'}
          </button>

          {bulkDownloadMode && (
            <>
              <button onClick={selectAllImages} className="select-all-images-btn">
                {selectedImages.size === displayImages.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={handleBulkDownload}
                disabled={selectedImages.size === 0 || downloading}
                className="bulk-download-btn"
              >
                {downloading ? (
                  <>
                    <Loader className="spin" size={18} />
                    Creating ZIP...
                  </>
                ) : (
                  <>
                    <Download size={18} />
                    Download as ZIP ({selectedImages.size})
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      <div className="wedding-grid">
        {displayImages.map((img, index) => (
          <div
            key={index}
            className={`wedding-grid-item ${loadedImages.has(img.url) ? 'loaded' : ''} ${selectedImages.has(index) ? 'selected-for-download' : ''}`}
            ref={(el) => (imageCardsRef.current[index] = el)}
            onClick={() => {
              if (bulkDownloadMode) {
                toggleImageSelection(index);
              } else {
                openLightbox(img, displayImages);
              }
            }}
          >
            {bulkDownloadMode && (
              <div className="image-select-checkbox">
                {selectedImages.has(index) ? (
                  <CheckSquare size={24} className="checked" />
                ) : (
                  <Square size={24} />
                )}
              </div>
            )}
            <div className="image-number-overlay">#{index + 1}</div>
            <img
              src={fixImageUrl(img.url)}
              alt={img.name}
              loading={index < 8 ? "eager" : "lazy"}
              onLoad={() => handleImageLoad(img.url)}
            />
            {!loadedImages.has(img.url) && (
              <div className="image-placeholder">
                <Loader className="spin" size={24} />
              </div>
            )}
            {!bulkDownloadMode && (
              <button
                className={`favorite-btn ${favorites.has(img.url) ? 'active' : ''}`}
                onClick={(e) => toggleFavorite(img.url, e)}
              >
                <Heart size={20} fill={favorites.has(img.url) ? "currentColor" : "none"} />
              </button>
            )}
          </div>
        ))}
      </div>

      {lightboxImage && (
        <div
          className="wedding-lightbox-fullscreen"
          onClick={closeLightbox}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="lightbox-header">
            <div className="lightbox-counter">
              {lightboxIndex + 1} / {displayImages.length}
            </div>
            <div className="lightbox-actions">
              <button
                className={`lightbox-action-btn ${favorites.has(lightboxImage.url) ? 'active' : ''}`}
                onClick={(e) => toggleFavorite(lightboxImage.url, e)}
              >
                <Heart size={20} fill={favorites.has(lightboxImage.url) ? "currentColor" : "none"} />
              </button>
              <button
                className="lightbox-download-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(lightboxImage.url, lightboxImage.name);
                }}
              >
                <Download size={20} />
              </button>
              <button className="lightbox-close-btn" onClick={closeLightbox}>
                <X size={24} />
              </button>
            </div>
          </div>

          <button
            className="lightbox-nav-btn prev"
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox('prev', displayImages);
            }}
          >
            <ChevronLeft size={32} />
          </button>

          <button
            className="lightbox-nav-btn next"
            onClick={(e) => {
              e.stopPropagation();
              navigateLightbox('next', displayImages);
            }}
          >
            <ChevronRight size={32} />
          </button>

          <div className="lightbox-image-container" onClick={(e) => e.stopPropagation()}>
            <img
              src={fixImageUrl(lightboxImage.url)}
              alt={lightboxImage.name}
              className="lightbox-fullscreen-image"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default WeddingDetail;